import React, { useEffect, useState } from 'react';
import { forwardRef, useImperativeHandle } from 'react';
import { useDispatch } from 'react-redux';
import { Form } from '@ant-design/compatible';
import { FormComponentProps } from '@ant-design/compatible/lib/form';
import produce from 'immer';

import Table from '../../../../../components/_table/Table/Table';
import ExcelUploadForm from '../../../../../components/form/ExcelUploadForm';
import Anchor from '../../../../../components/link/Anchor';
import Button from '../../../../../components/Button';
import TagListModal from './TagListModal';
import { columns } from './columns';
import { applyFilterOption, applyPagination, convertToTableData } from './utils';
import useExcel from './hooks/useExcel';
import { appActions } from '../../../../../store/modules/app';
import { EventDetailTagItem } from '../../../../../types/query/event/detail';
import { TableOption } from './types';
import { TagItem } from '../../types';
import { INITIAL_OPTION_MODEL } from './constants';
import { TAG_UPLOAD_TEMPLATE_XLSX_URL } from '../../../../../constants/service/common/staticFileUrlConstants';
import { SelectProductAndTagListStyle } from '../common/style';

interface SelectTagListRef {
  value?: TagItem[];
  onChange?: (value: TagItem[]) => void;
}
interface SelectTagListProps {
  value?: TagItem[];
  onChange?: (value: TagItem[]) => void;
}
const SelectTagList = forwardRef<SelectTagListRef, SelectTagListProps>((props, ref) => {
  const { value = [], onChange } = props;
  const [isTagListModal, setIsTagListModal] = useState(false);
  const [checkedIdList, setCheckedIdList] = useState<number[]>([]);
  const [tableOption, setTableOption] = useState<TableOption>(INITIAL_OPTION_MODEL);
  const { filterOption, pagination } = tableOption;
  const { isExcelUploadSuccess, downloadSelectedExcel, uploadExcel } = useExcel({ value, onChange });
  const dispatch = useDispatch();

  useImperativeHandle(ref, () => ({
    value,
    onChange,
  }));

  useEffect(() => {
    if (!isExcelUploadSuccess) return;

    resetOption();
  }, [isExcelUploadSuccess]);

  const resetOption = () => {
    setTableOption(INITIAL_OPTION_MODEL);
  };

  const openTagListModal = () => {
    setIsTagListModal(true);
  };

  const addTagList = (newTagItem: TagItem[]) => {
    if (!onChange) return;

    onChange([...newTagItem, ...value]);
    resetOption();
  };

  const deleteTagList = () => {
    if (!onChange) return;

    const checkedIdListSet = new Set(checkedIdList);
    setCheckedIdList((prev) => prev.filter((item) => !checkedIdListSet.has(item)));
    const deletedValue = value.filter((item) => !checkedIdListSet.has(item.id));

    onChange(deletedValue);
    setTableOption((prevValue) => {
      return produce(prevValue, (draft) => {
        // 현재 페이지에 태그가 모두 삭제되었을 경우 이전 페이지를 가리켜야함
        const currentPage = draft.pagination.pageNum;
        const totalPage = Math.ceil(deletedValue.length / pagination.pageSize) - 1;
        if (totalPage < 0) {
          draft.pagination.pageNum = 0;
        } else if (currentPage > totalPage) {
          draft.pagination.pageNum = totalPage;
        } else {
          draft.pagination.pageNum = currentPage;
        }
      });
    });
  };

  const getRelatedTagList = (data: { tagId: number }) => {
    dispatch(appActions.getRelatedTagList(data));
  };

  const listWithAppliedFilterOptions = applyFilterOption(value, filterOption);
  const visibleList = applyPagination(listWithAppliedFilterOptions, pagination);
  return (
    <>
      <SelectProductAndTagListStyle>
        <div className="item-counter">
          등록된 태그 개수 : <span>{value.length}</span> ㅣ 선택된 태그 개수 : <span>{checkedIdList.length}</span>
        </div>
        <div className="button-wrap">
          <div className="right">
            <Button isRadius color="black" text="태그 추가" onClick={openTagListModal} />
            <Button isRadius color="light" text="필터 초기화" onClick={resetOption} />
          </div>
          <div className="left">
            <Anchor
              text="태그 업로드 템플릿"
              isUnderLine
              color="primary"
              href={TAG_UPLOAD_TEMPLATE_XLSX_URL}
              target="_blank"
            />

            <ExcelUploadForm onChange={(file: File) => uploadExcel(file)} btnText="엑셀 업로드" />

            <Button isRadius color="light" text="엑셀 다운로드" onClick={() => downloadSelectedExcel()} />
          </div>
        </div>
        <Table
          rowKey={(record: TagItem) => record.id}
          columns={(utils: any) => columns({ ...utils, getRelatedTagList })}
          list={visibleList}
          size="middle"
          scroll={{ x: true }}
          bordered={true}
          onCheck={(list: number[]) => setCheckedIdList(list)}
          selectedList={checkedIdList}
          getList={(data: TableOption) => {
            setTableOption({
              ...tableOption,
              ...data,
            });
          }}
          pagination={{
            ...pagination,
            totalCnt: listWithAppliedFilterOptions.length,
          }}
          filterOption={filterOption}
        />
        <div className="button-wrap">
          <Button isRadius color="primary" text="삭제" disabled={!checkedIdList.length} onClick={deleteTagList} />
        </div>
      </SelectProductAndTagListStyle>
      {isTagListModal && (
        <TagListModal
          closeModal={() => setIsTagListModal(false)}
          selectedTagIdList={value.map((item) => item.id)}
          onSubmit={addTagList}
          getRelatedTagList={getRelatedTagList}
        />
      )}
    </>
  );
});
SelectTagList.displayName = 'SelectTagList';

interface SelectTagListFormProps extends FormComponentProps {
  name: string;
  initialValue?: EventDetailTagItem[];
}
const SelectTagListForm = (props: SelectTagListFormProps) => {
  const { form, name, initialValue } = props;
  const { getFieldDecorator } = form;

  return (
    <div>
      <Form.Item>
        {getFieldDecorator(name, {
          initialValue: convertToTableData(initialValue),
        })(<SelectTagList />)}
      </Form.Item>
    </div>
  );
};

export default SelectTagListForm;
